import React from 'react';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: October 4, 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to Naat City. We are committed to protecting your privacy and ensuring transparency 
            about how we collect, use, and share your information. This Privacy Policy explains our 
            practices regarding data collection and usage when you use our website.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide</h3>
          <p>
            We may collect information that you voluntarily provide when using our services, such as:
          </p>
          <ul>
            <li>Search queries and preferences</li>
            <li>Favorites and playlist information stored locally in your browser</li>
            <li>Feedback or communications you send to us</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <p>
            When you visit our website, we automatically collect certain information through Google Analytics, including:
          </p>
          <ul>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Pages visited and time spent on pages</li>
            <li>Referring website addresses</li>
            <li>IP address (anonymized)</li>
            <li>Device type and screen resolution</li>
            <li>General geographic location (country/city level)</li>
          </ul>
        </section>

        <section>
          <h2>3. Use of Google Analytics</h2>
          <p>
            We use Google Analytics, a web analytics service provided by Google LLC ("Google"), to help 
            us understand how visitors use our website. Google Analytics uses cookies and similar technologies 
            to collect and analyze information about website usage.
          </p>
          
          <h3>3.1 What Google Analytics Collects</h3>
          <p>Google Analytics collects information such as:</p>
          <ul>
            <li>How often users visit our website</li>
            <li>What pages they visit and in what sequence</li>
            <li>How long they stay on each page</li>
            <li>What website referred them to us</li>
            <li>Demographic information (when available)</li>
          </ul>

          <h3>3.2 How We Use Google Analytics Data</h3>
          <p>We use the information collected by Google Analytics to:</p>
          <ul>
            <li>Improve our website content and user experience</li>
            <li>Understand user behavior and preferences</li>
            <li>Identify popular content and features</li>
            <li>Optimize website performance</li>
            <li>Make informed decisions about website improvements</li>
          </ul>

          <h3>3.3 Google Analytics IP Anonymization</h3>
          <p>
            We have enabled IP anonymization in Google Analytics. This means that Google anonymizes your 
            IP address as soon as data is received by the Analytics Collection Network, before any storage 
            or processing takes place.
          </p>
        </section>

        <section>
          <h2>4. Cookies and Similar Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to enhance your experience on our website. 
            Cookies are small text files stored on your device that help us remember your preferences 
            and understand how you use our website.
          </p>

          <h3>4.1 Types of Cookies We Use</h3>
          <ul>
            <li>
              <strong>Essential Cookies:</strong> Required for the website to function properly, 
              including storing your favorites and preferences locally.
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Google Analytics cookies that help us understand 
              how visitors interact with our website.
            </li>
            <li>
              <strong>Functional Cookies:</strong> Remember your preferences and settings to improve 
              your experience.
            </li>
          </ul>

          <h3>4.2 Managing Cookies</h3>
          <p>
            You can control and manage cookies through your browser settings. Most browsers allow you to:
          </p>
          <ul>
            <li>View and delete cookies</li>
            <li>Block all cookies</li>
            <li>Block third-party cookies</li>
            <li>Clear cookies when you close your browser</li>
          </ul>
          <p>
            Please note that blocking or deleting cookies may impact your experience on our website 
            and prevent certain features from working properly.
          </p>
        </section>

        <section>
          <h2>5. How We Use Your Information</h2>
          <p>We use the collected information for the following purposes:</p>
          <ul>
            <li>To provide and maintain our service</li>
            <li>To improve and personalize user experience</li>
            <li>To analyze usage patterns and trends</li>
            <li>To troubleshoot technical issues</li>
            <li>To communicate with you about updates or changes</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Sharing and Disclosure</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties. We may share 
            information in the following circumstances:
          </p>
          <ul>
            <li>
              <strong>With Google:</strong> Analytics data is shared with Google as part of their 
              Analytics service. Google's use of this data is governed by their{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to protect our rights 
              and safety.
            </li>
            <li>
              <strong>Service Providers:</strong> With trusted service providers who assist us in 
              operating our website, subject to confidentiality obligations.
            </li>
          </ul>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          <p>You have the following rights regarding your data:</p>
          
          <h3>7.1 Opt-Out of Google Analytics</h3>
          <p>You can opt-out of Google Analytics tracking by:</p>
          <ul>
            <li>
              Installing the{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                Google Analytics Opt-out Browser Add-on
              </a>
            </li>
            <li>Enabling "Do Not Track" in your browser settings</li>
            <li>Blocking analytics cookies through your browser settings</li>
          </ul>

          <h3>7.2 Data Access and Deletion</h3>
          <p>
            Since we primarily use local storage for your preferences and favorites, you can delete 
            this data at any time by clearing your browser's local storage or cookies.
          </p>

          <h3>7.3 European Users (GDPR)</h3>
          <p>If you are located in the European Economic Area, you have additional rights including:</p>
          <ul>
            <li>Right to access your personal data</li>
            <li>Right to rectify inaccurate data</li>
            <li>Right to erasure ("right to be forgotten")</li>
            <li>Right to restrict processing</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
          </ul>
        </section>

        <section>
          <h2>8. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your information 
            from unauthorized access, alteration, disclosure, or destruction. However, no method of 
            transmission over the internet or electronic storage is 100% secure, and we cannot guarantee 
            absolute security.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our website is not directed to children under the age of 13. We do not knowingly collect 
            personal information from children under 13. If you believe we have collected information 
            from a child under 13, please contact us immediately.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites. We are not responsible for the 
            privacy practices or content of these external sites. We encourage you to review the 
            privacy policies of any third-party sites you visit.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices 
            or legal requirements. We will notify you of any material changes by posting the updated 
            policy on this page with a new "Last Updated" date. Your continued use of our website 
            after any changes indicates your acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>12. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country 
            of residence. These countries may have different data protection laws. By using our website, 
            you consent to the transfer of your information to these countries.
          </p>
        </section>

        <section>
          <h2>13. Consent</h2>
          <p>
            By using our website, you consent to the collection and use of information as described 
            in this Privacy Policy. If you do not agree with this policy, please do not use our website.
          </p>
        </section>

        <section className="policy-footer">
          <p>
            This Privacy Policy complies with applicable data protection laws including the General 
            Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and Google 
            Analytics Terms of Service.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
